import {
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  makeStyles,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@material-ui/core';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router';
import { createAlert } from '../../redux/actions/user';
import RTVDialog from '../../commons/dialogs/RTVDialog';
import AdornedButton from '../../commons/AdornedButton';
import {
  CREATE_ALERT_ERRORED,
  CREATE_ALERT_REQUESTED,
  CREATE_ALERT_SUCCESSFULL
} from '../../redux/actions/types';
import numberWithCommas from '../../utils/numberWithCommas';
import { useTranslation } from 'react-i18next';
import { expressToggle } from '../../redux/actions/app';

const useStyles = makeStyles(theme => ({
  container: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  dialogContent: {
    width: '100%',
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  },
  dialogTitle: {
    textAlign: 'center',
    fontSize: '1.5rem',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center'
  },
  filterChips: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    '& > *': {
      margin: theme.spacing(1)
    }
  },
  aliasField: {
    width: '250px'
  },
  disabledButton: {
    pointerEvents: 'all !important',
    cursor: 'pointer !important'
  }
}));

const translateCondition = condition => {
  switch (condition) {
    case 'CONDITION_LIKE_NEW': {
      return 'Como nuevo';
    }
    case 'CONDITION_NEW_WITH_TAG': {
      return 'Nuevo con etiqueta';
    }
    case 'CONDITION_NEW_WITHOUT_TAG': {
      return 'Nuevo sin etiqueta';
    }
    case 'CONDITION_USED': {
      return 'Usado';
    }
    default:
      return '';
  }
};

const translateRole = role => {
  switch (role) {
    case 'ROLE_PREMIUM':
      return 'Premium';
    case 'ROLE_VIP':
      return 'VIP';
    case 'ROLE_CELEBRITY':
      return 'Celebrities';
    case 'ROLE_SHOWROOM':
      return 'Showroom';
    case 'ROLE_MARKETPLACE':
      return 'Marketplace';
    default:
      return role;
  }
};

const translatePromotion = promotion => {
  switch (promotion) {
    case 'TWO_FOR_ONE':
      return '2x1';
    case 'THREE_FOR_TWO':
      return '3x2';
    case 'TWENTY_PERCENT':
      return '20% Extra';
    case 'THIRTY_PERCENT':
      return '30% Extra';
    case 'FORTY_PERCENT':
      return '40% Extra';
    default:
      return promotion;
  }
};

const translateShipping = shipping => {
  switch (shipping) {
    case 'ADDRESS':
      return 'A domicilio';
    case 'OCA':
      return 'Sucursales OCA';
    case 'CORREO ARGENTINO':
      return 'Sucursales Correo Argentino';
    case 'OFFICE':
      return 'Retiro sin cargo (CABA)';
    default:
      return shipping;
  }
};

const filtersCount = filters => {
  let count = 0;

  if (filters.category && !filters.sub_category) {
    count += 1;
  }
  if (filters.sub_category && filters.sub_sub_category.length === 0) {
    count += 1;
  }
  if (filters.minPrice) {
    count += 1;
  }
  if (filters.maxPrice) {
    count += 1;
  }
  if (filters.users === 'followings') {
    count += 1;
  }
  count += filters.sub_sub_category.length;
  count += filters.brand.length;
  count += filters.size.length;
  count += filters.promo.length;
  count += filters.shipping.length;
  count += filters.role.length;
  count += filters.condition.length;
  count += filters.color.length;
  count += filters.tags.length;

  return count;
};

const Alerts = ({ appliedFilters, visible, ...props }) => {
  const { t } = useTranslation();
  const FREQUENCIES = { DAILY: 24, WEEKLY: 168 };
  const location = useLocation();
  const isLoggedIn = useSelector(state => state.auth.isLoggedIn);
  const error = useSelector(state => state.user.error);
  const status = useSelector(state => state.user.status);
  const user = useSelector(state => state.auth.user);
  const categories = useSelector(state => state.categories.categories);
  const brands = useSelector(state => state.categories.brands);
  const sizes = useSelector(state => state.categories.allSizes);
  const colors = useSelector(state => state.categories.colors);
  const tags = useSelector(state => state.categories.tags);
  const [allowNewAlert, setAllowNewAlert] = useState(false);
  const [frequency, setFrequency] = useState(FREQUENCIES.DAILY);
  const [alias, setAlias] = useState('');
  const [aliasError, setAliasError] = useState({ error: false, message: ' ' });
  const [openDialog, setOpenDialog] = useState(false);
  const [loadingPost, setLoadingPost] = useState(false);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [openDuplicatedAlert, setOpenDuplicatedAlert] = useState(false);
  const [openMinFilters, setOpenMinFilters] = useState(false);
  const classes = useStyles();
  const dispatch = useDispatch();

  useEffect(() => {
    if (filtersCount(appliedFilters) < 2) {
      setAllowNewAlert(false);
    } else {
      setAllowNewAlert(true);
    }
  }, [appliedFilters]);

  useEffect(() => {
    if (status === CREATE_ALERT_REQUESTED) {
      setLoadingPost(true);
    }
    if (status === CREATE_ALERT_SUCCESSFULL) {
      setLoadingPost(false);
      setOpenDialog(false);
      setOpenSuccessDialog(true);
    }
    if (status === CREATE_ALERT_ERRORED) {
      setLoadingPost(false);
      if (error.status === 409) {
        setOpenDialog(false);
        setOpenDuplicatedAlert(true);
      } else if (error.status === 410) {
        setAliasError({
          error: true,
          message: 'Ya tenés otra alerta con el mismo título'
        });
      } else if (error.status === 400) {
        setOpenDialog(false);
        setOpenMinFilters(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const handleSubmit = e => {
    e.preventDefault();
    const {
      user: _user,
      users: userFilter,
      subTypes,
      ...filters
    } = appliedFilters;
    if (userFilter === 'followings') {
      filters['followings'] = user.id;
    }
    if (alias.trim() === '') {
      setAliasError({
        error: true,
        message: 'El título de la alerta es requerido'
      });
      return;
    }
    const alert = {
      frequency,
      filters,
      alias
    };
    dispatch(createAlert(alert));
  };

  const handleAliasChange = ({ target: { value } }) => {
    setAliasError({ error: false, message: ' ' });
    setAlias(value);
  };

  if (!visible) {
    return null;
  }

  const renderFilters = () => {
    let category;
    let subCategory;
    if (appliedFilters.category) {
      category = categories.find(c => c.id === appliedFilters.category);
    }
    if (category && appliedFilters.sub_category) {
      subCategory = category.sub_categories.find(
        c => c.id === appliedFilters.sub_category
      );
    }
    const subSubCategories =
      subCategory && appliedFilters.sub_sub_category.length > 0
        ? subCategory.sub_sub_categories.filter(
            c => appliedFilters.sub_sub_category.indexOf(c.id) !== -1
          )
        : [];

    return (
      <div className={classes.filterChips}>
        {/* Category applied filter */}
        {category !== undefined && subCategory === undefined && (
          <Chip label={category.name} />
        )}
        {/* SubCategory applied filter */}
        {subCategory !== undefined && subSubCategories.length === 0 && (
          <Chip label={`${category.name} > ${subCategory.name}`} />
        )}
        {/* SubSubCategories applied filter */}
        {subSubCategories.map(subSubCategory => (
          <Chip
            key={subSubCategory.id}
            label={`${category.name} > ${subCategory.name} > ${subSubCategory.name}`}
          />
        ))}
        {/* Brands applied filter */}
        {brands
          .filter(b => appliedFilters.brand.includes(b.id))
          .map(brand => (
            <Chip key={brand.id} label={brand.name} />
          ))}
        {/* Tags applied filter */}
        {tags
          .filter(t => appliedFilters.tags.includes(t.id))
          .map(tag => (
            <Chip key={tag.id} label={tag.name} />
          ))}
        {/* Colors applied filter */}
        {colors
          .filter(c => appliedFilters.color.includes(c.id))
          .map(color => (
            <Chip key={color.id} label={color.name} />
          ))}
        {/* Sizes applied filter */}
        {sizes
          .filter(s => appliedFilters.size.includes(s.id))
          .map(size => (
            <Chip key={size.id} label={size.name} />
          ))}
        {/* Conditions applied filter */}
        {appliedFilters.condition.map(condition => (
          <Chip key={condition} label={translateCondition(condition)} />
        ))}
        {/* Roles applied filter */}
        {appliedFilters.role.map(role => (
          <Chip key={role} label={translateRole(role)} />
        ))}
        {appliedFilters.users === 'followings' && (
          <Chip label={t('dressersYouFollow', { ns: 'shop' })} />
        )}
        {/* Promos applied filter */}
        {appliedFilters.promo.map(promo => (
          <Chip key={promo} label={translatePromotion(promo)} />
        ))}
        {/* Shippings applied filter */}
        {appliedFilters.shipping.map(shipping => (
          <Chip key={shipping} label={translateShipping(shipping)} />
        ))}
        {appliedFilters.minPrice && (
          <Chip
            label={`Más de $${numberWithCommas(appliedFilters.minPrice)}`}
          />
        )}
        {appliedFilters.maxPrice && (
          <Chip
            label={`Menos de $${numberWithCommas(appliedFilters.maxPrice)}`}
          />
        )}
      </div>
    );
  };

  const handleClick = () => {
    if (!isLoggedIn) {
      // TODO: implement the `Search` entity to return the user to the search once logged in
      dispatch(
        expressToggle(true, {
          entity: 'Search',
          identifier: `${location.pathname}${location.search}`
        })
      );
      return;
    }

    if (!allowNewAlert) {
      setOpenMinFilters(true);
      return;
    }

    setOpenDialog(true);
  };

  return (
    <div className={classes.container}>
      <RTVDialog
        open={openDuplicatedAlert}
        onClose={() => setOpenDuplicatedAlert(false)}
        title="Uups!"
        message="Ya tenes creada una alerta creada para estos filtros"
        actionSection={
          <>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenDuplicatedAlert(false)}
            >
              OK
            </Button>
            <Button
              component={Link}
              to="/mis-compras/mis-alertas"
              variant="contained"
              color="secondary"
            >
              Administrar mis alertas
            </Button>
          </>
        }
      />
      <RTVDialog
        open={openMinFilters}
        onClose={() => setOpenMinFilters(false)}
        title={t('selectMoreFilter', { ns: 'shop' })}
        message={t('minAmountOfFilters', { ns: 'shop' })}
        actionSection={
          <>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenMinFilters(false)}
            >
              OK
            </Button>
          </>
        }
      />
      <RTVDialog
        open={openSuccessDialog}
        onClose={() => setOpenSuccessDialog(false)}
        title={`Configuraste tu alerta "${alias}"!`}
        message={`Te vamos a avisar ${
          frequency === FREQUENCIES.DAILY ? 'diariamente' : 'semanalmente'
        } ${t('successfulAlertMessage', { ns: 'shop' })}`}
        actionSection={
          <>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenSuccessDialog(false)}
            >
              OK
            </Button>
            <Button
              variant="contained"
              color="secondary"
              component={Link}
              to="/mis-compras/mis-alertas"
            >
              Administrar alertas
            </Button>
          </>
        }
      />
      <RTVDialog
        open={openDialog}
        onClose={() => !loadingPost && setOpenDialog(false)}
        content={
          <div className={classes.dialogContent}>
            <Typography color="primary" className={classes.dialogTitle}>
              <NotificationsActiveIcon />
              Crear Alerta
            </Typography>
            <div className={classes.dialogContent}>
              <Typography style={{ fontWeight: 600 }}>
                Estás creando una alerta para
              </Typography>
              {renderFilters()}
            </div>
            <form onSubmit={handleSubmit} className={classes.dialogContent}>
              <Typography style={{ fontWeight: 600 }}>
                Título del alerta
              </Typography>
              <TextField
                error={aliasError.error}
                value={alias}
                onChange={handleAliasChange}
                helperText={aliasError.message}
                className={classes.aliasField}
              />
              <Typography style={{ fontWeight: 600, marginTop: '16px' }}>
                {t('frequency', { ns: 'shop' })}
              </Typography>
              <FormControl>
                <RadioGroup
                  value={frequency}
                  onChange={e => setFrequency(Number(e.target.value))}
                >
                  <FormControlLabel
                    value={FREQUENCIES.DAILY}
                    control={<Radio color="primary" />}
                    label="Diaria: 1 vez por día con todas las prendas nuevas"
                  />
                  <FormControlLabel
                    value={FREQUENCIES.WEEKLY}
                    control={<Radio color="primary" />}
                    label="Semanal: 1 vez por semana con todas las prendas nuevas"
                  />
                </RadioGroup>
              </FormControl>
              <AdornedButton
                variant="contained"
                color="primary"
                type="submit"
                loading={loadingPost}
                disabled={loadingPost}
              >
                Guardar
              </AdornedButton>
            </form>
          </div>
        }
      />
      <Button
        variant="contained"
        color="primary"
        className={
          allowNewAlert ? '' : `Mui-disabled ${classes.disabledButton}`
        }
        onClick={handleClick}
      >
        <NotificationsActiveIcon />
        Crear alerta
      </Button>
    </div>
  );
};

export default React.memo(Alerts);
